/**
 * Public product showcase catalog — mirrors landing/dashboard modules.
 * Icons are Lucide name keys resolved in the page components.
 */

export const PRODUCT_BRAND = 'Snagcart';

export const productShowcases = [
  {
    slug: 'websites',
    icon: 'Wand2',
    tag: 'Core',
    title: 'My Websites',
    subtitle: 'AI Website Builder',
    eyebrow: 'Build & publish',
    hero: 'Launch beautiful websites with AI — from brief to live in minutes.',
    desc: 'Build full-stack websites with AI. Use the step-by-step Snagcart Builder or start from a professionally designed template.',
    features: [
      'Snagcart Builder — planner → spec → code',
      'Create from template library',
      'Visual drag-and-drop editor',
      'AI Page Assistant & Site Brain',
      'Website preview & one-click publish',
      'Custom domain connection',
    ],
    benefits: [
      { title: 'Ship faster', body: 'Go from idea to published site without a full engineering team.' },
      { title: 'Edit visually', body: 'Refine layouts and copy in a familiar editor — then publish.' },
      { title: 'Own your brand', body: 'Connect custom domains and keep everything under your name.' },
    ],
    loginUrl: '/auth',
    appPath: '/websites',
  },
  {
    slug: 'snagbuilder',
    icon: 'Rocket',
    tag: 'New',
    title: 'Snag Builder',
    subtitle: 'AI Full-stack App Builder',
    eyebrow: 'Prompt → product',
    hero: 'One prompt becomes a full-stack app with live preview you can keep editing.',
    desc: 'Snag Builder turns a single prompt into a full-stack website or app — it plans, specs, writes the code and shows you a live preview.',
    features: [
      'Prompt → full working app',
      'AI planner → spec → code',
      'Live sandbox preview',
      'Iterate and refine with AI',
      'One-click deploy & publish',
      'Start fresh or bring your own code',
    ],
    benefits: [
      { title: 'From idea to demo', body: 'Show stakeholders a working preview the same day.' },
      { title: 'Keep iterating', body: 'Chat with the builder to refine UX, copy, and flows.' },
      { title: 'Deploy when ready', body: 'Publish when quality gates pass — not before.' },
    ],
    loginUrl: '/auth',
    appPath: '/snagbuilder',
  },
  {
    slug: 'ai-cofounder',
    icon: 'Sparkles',
    tag: 'Never Sleeps',
    title: 'AI Co-founder',
    subtitle: 'AI agents that never sleep',
    eyebrow: 'Always-on company OS',
    hero: 'AI agents that never sleep — your co-founder that plans, drafts, and runs growth while you rest.',
    desc: 'Launch a brand workspace with AI agents that never sleep — running CRM, marketing, builder, SEO, social, and support loops around the clock, with approvals for send, publish, and deploy. Included on all plans with tiered autonomy.',
    features: [
      'AI agents that never sleep — 24/7 growth loops',
      'Included on Basic, Starter, and Growth (tiered autonomy)',
      'Basic: plan & draft teaser · 1 cycle/day',
      'Starter: ≈10 cycles/day · real lead scrape with credits',
      'Growth: always-on · multi-brand companies',
      'Approval gates for send, publish, and deploy',
    ],
    benefits: [
      { title: 'Never sleeps', body: 'AI agents keep growth loops moving overnight — you wake up to drafts ready to approve.' },
      { title: 'Safe by default', body: 'Nothing sends or publishes until you approve.' },
      { title: 'One Results hub', body: 'Every artifact opens in the real CRM, Builder, or Marketing module.' },
    ],
    loginUrl: '/auth',
    appPath: '/ai-cofounder',
  },
  {
    slug: 'ai-tools',
    icon: 'Sparkles',
    tag: '22 Tools',
    title: 'AI Tools',
    subtitle: '9 Categories · 22 Tools',
    eyebrow: 'Creative suite',
    hero: 'A complete AI creative suite — content, media, audio, video, bots, and docs.',
    desc: 'A complete AI creative suite built into your workspace — content, media, audio, video, bots, documents and more.',
    features: [
      'Content: Writer, Editor, ReWriter, Article Wizard',
      'Media: Image, Photo Studio, Product Photography, Avatar, Vision',
      'Audio: Voiceover, Speech to Text',
      'Video: AI Video Generator, AI YouTube',
      'Bots: Smart Bots, Voice Bot',
      'Docs: File Chat, Web Chat, RSS, Presentation',
    ],
    benefits: [
      { title: 'One workspace', body: 'Stop hopping between disconnected AI apps.' },
      { title: 'On-brand output', body: 'Generate assets that match your product voice.' },
      { title: 'Team ready', body: 'Share tools across your Snagcart workspace.' },
    ],
    loginUrl: '/auth',
    appPath: '/ai-tools-dashboard',
  },
  {
    slug: 'media-studio',
    icon: 'Image',
    tag: 'Creative',
    title: 'Media Studio',
    subtitle: 'AI Image · Video · Audio',
    eyebrow: 'Create assets',
    hero: 'Generate on-brand images, video, avatars, and voiceovers from one studio.',
    desc: 'Generate on-brand images, product photography, videos, avatars and voiceovers from a single creative studio — no designer needed.',
    features: [
      'AI image & product photography',
      'AI video generator',
      'Voiceover & speech-to-text',
      'AI avatars & vision',
      'Brand-consistent assets',
      'Export in any format',
    ],
    benefits: [
      { title: 'Production speed', body: 'Create campaign creatives without a design backlog.' },
      { title: 'Consistent brand', body: 'Reuse styles across image, video, and audio.' },
      { title: 'Ready to publish', body: 'Export assets straight into Ads and Marketing.' },
    ],
    loginUrl: '/auth',
    appPath: '/media-studio',
  },
  {
    slug: 'bot-leads',
    icon: 'Bot',
    tag: 'Lead Capture',
    title: 'Bot Leads & Calls',
    subtitle: 'Smart Bots + Voice AI',
    eyebrow: 'Capture 24/7',
    hero: 'AI chat and voice bots that capture leads and book appointments around the clock.',
    desc: 'Deploy AI chatbots and voice bots on your website to capture leads, book appointments and handle inbound calls 24/7.',
    features: [
      'AI Smart Bots with custom knowledge base',
      'AI Voice Bot for inbound calls',
      'Automatic lead capture from conversations',
      'Calendar appointment booking',
      'Call transcripts & recordings',
      'Seamless CRM handoff',
    ],
    benefits: [
      { title: 'Never miss a lead', body: 'Respond instantly even when your team is offline.' },
      { title: 'Qualify automatically', body: 'Bots collect the details your sales team needs.' },
      { title: 'Sync to CRM', body: 'Every conversation can become a CRM record.' },
    ],
    loginUrl: '/auth',
    appPath: '/bot-leads',
  },
  {
    slug: 'voice',
    icon: 'Phone',
    tag: 'Outbound',
    title: 'Voice Campaigns',
    subtitle: 'AI Outbound Calling',
    eyebrow: 'Dial at scale',
    hero: 'Run personalized AI outbound calling campaigns with transcripts and tracking.',
    desc: 'Build your AI voice bot and run automated outbound campaigns. Reach leads with personalised calls — one Voice module.',
    features: [
      'AI voice bot builder + outbound campaigns',
      'Multi-contact dialing with Twilio BYOK',
      'Real-time campaign status tracking',
      'Automatic call transcripts',
      'Lead qualification via voice',
      'Schedule campaigns for optimal times',
    ],
    benefits: [
      { title: 'Scale outreach', body: 'Reach more prospects without burning your team out.' },
      { title: 'Bring your own Twilio', body: 'Keep control of telephony credentials and spend.' },
      { title: 'Full visibility', body: 'Track status, transcripts, and outcomes in one place.' },
    ],
    loginUrl: '/auth',
    appPath: '/voice-campaigns',
  },
  {
    slug: 'domains',
    icon: 'Globe',
    tag: 'Infrastructure',
    title: 'Domains & Hosting',
    subtitle: 'Domain Registration + Hosting',
    eyebrow: 'Go live',
    hero: 'Search, register, and connect custom domains to your Snagcart sites.',
    desc: 'Search, register and manage custom domains for all your websites. Connect them instantly to your Snagcart-built sites.',
    features: [
      'Domain search across all TLDs',
      'One-click domain registration',
      'My Domains management panel',
      'Hosting plans included',
      'Auto-connect to your websites',
      'SSL certificates included',
    ],
    benefits: [
      { title: 'Professional presence', body: 'Launch on your brand domain in minutes.' },
      { title: 'Less DNS pain', body: 'Connect domains without juggling providers.' },
      { title: 'Secure by default', body: 'SSL included so customers trust every click.' },
    ],
    loginUrl: '/auth',
    appPath: '/domains-hosting',
  },
  {
    slug: 'marketing',
    icon: 'Megaphone',
    tag: 'Multi-channel',
    title: 'Marketing Suite',
    subtitle: 'Email · SMS · WhatsApp',
    eyebrow: 'Reach everywhere',
    hero: 'Run Email, SMS, and WhatsApp campaigns from one marketing workspace.',
    desc: 'Unified multi-channel marketing platform. Run campaigns, manage contacts and track results across Email, SMS and WhatsApp.',
    features: [
      'Email Marketing — campaigns & templates',
      'SMS Marketing — bulk messaging',
      'WhatsApp — broadcasts, chatbots, warmer',
      'WhatsApp contacts, chats & tickets',
      'Unified CRM inbox',
      'Campaign analytics & blacklist management',
    ],
    benefits: [
      { title: 'One inbox story', body: 'Keep customer conversations connected across channels.' },
      { title: 'Campaign control', body: 'Draft, approve, and send with clear status tracking.' },
      { title: 'Growth loops', body: 'Pair with Lead Scraper and Co-founder for end-to-end outreach.' },
    ],
    loginUrl: '/auth',
    appPath: '/marketing-suite',
  },
  {
    slug: 'ai-seo',
    icon: 'TrendingUp',
    tag: 'Growth',
    title: 'AI SEO',
    subtitle: 'Rank higher, on autopilot',
    eyebrow: 'Organic growth',
    hero: 'Research keywords, generate content, and run SEO campaigns on autopilot.',
    desc: 'Research keywords, generate optimised content and run automated SEO campaigns that grow your organic traffic month after month.',
    features: [
      'Keyword research & clustering',
      'AI-optimised content generation',
      'On-page SEO audits & fixes',
      'Automated SEO campaigns',
      'Rank tracking & reporting',
      'Technical SEO recommendations',
    ],
    benefits: [
      { title: 'Compound traffic', body: 'Turn SEO into a repeating system, not a one-off audit.' },
      { title: 'Actionable fixes', body: 'See what to change — and apply when you approve.' },
      { title: 'Content at scale', body: 'Generate optimised pages that match search intent.' },
    ],
    loginUrl: '/auth',
    appPath: '/marketing-suite/seo',
  },
  {
    slug: 'ai-ads',
    icon: 'Target',
    tag: 'Paid',
    title: 'AI Ads',
    subtitle: 'AI-generated ad campaigns',
    eyebrow: 'Paid media',
    hero: 'Create high-converting creatives and campaign plans for Meta and Google.',
    desc: 'Create high-converting image and video ads, generate copy and launch campaigns across Meta and Google — all powered by AI.',
    features: [
      'AI image & video ad creatives',
      'Ad copy generation',
      'Multi-language ads',
      'Social scheduler & publishing',
      'Ready-made campaign templates',
      'Performance-ready exports',
    ],
    benefits: [
      { title: 'Creative velocity', body: 'Produce more ad variants without a creative bottleneck.' },
      { title: 'Channel ready', body: 'Export assets formatted for the platforms you use.' },
      { title: 'Human approval', body: 'Publish and spend stay gated until you say yes.' },
    ],
    loginUrl: '/auth',
    appPath: '/marketing-suite/ads',
  },
  {
    slug: 'automations',
    icon: 'Workflow',
    tag: 'No-code',
    title: 'Automations',
    subtitle: 'Visual Workflow Builder',
    eyebrow: 'Connect everything',
    hero: 'Build trigger-based workflows that keep your tools in sync — no code required.',
    desc: 'Build powerful trigger-based automations without writing code. Connect your tools, set conditions and let workflows run.',
    features: [
      'Visual drag-and-drop workflow editor',
      'Trigger-based automation rules',
      'Connect any tool or API',
      'Conditional branching logic',
      'Schedule & time-based triggers',
      'Monitor runs in real time',
    ],
    benefits: [
      { title: 'Remove busywork', body: 'Hand repetitive ops to reliable workflows.' },
      { title: 'Cross-module glue', body: 'Connect CRM, marketing, and builder events.' },
      { title: 'See every run', body: 'Monitor successes and failures in real time.' },
    ],
    loginUrl: '/auth',
    appPath: '/automations',
  },
  {
    slug: 'ai-studio',
    icon: 'Zap',
    tag: 'Agents',
    title: 'AI Studio',
    subtitle: 'Custom AI Agents',
    eyebrow: 'Build agents',
    hero: 'Design, deploy, and monitor custom AI agents that run on your workflows.',
    desc: 'Build, deploy and monitor custom AI agents that run autonomously. Use pre-built templates or design your own from scratch.',
    features: [
      'Agent builder with full configuration',
      'Visual workflow node editor',
      'Live monitoring & agent logs',
      'Pre-built agent templates',
      'Connect to 100+ tools & APIs',
      'Deploy agents that run 24/7',
    ],
    benefits: [
      { title: 'Your playbooks, automated', body: 'Encode how your team works into agents.' },
      { title: 'Observable', body: 'Logs and monitoring keep autonomy accountable.' },
      { title: 'Extensible', body: 'Wire agents into Connections and Automations.' },
    ],
    loginUrl: '/auth',
    appPath: '/ai-studio',
  },
  {
    slug: 'lead-scraper',
    icon: 'Search',
    tag: 'Prospecting',
    title: 'Lead Scraper',
    subtitle: 'Multi-source Lead Generation',
    eyebrow: 'Find buyers',
    hero: 'Pull real local businesses from Google Maps and sync them straight into CRM.',
    desc: 'Pull high-intent leads from Google Maps and business directories. Auto-enrich with contact details and export directly to your CRM with one click.',
    features: [
      'Google Maps business scraping',
      'Business directory scraping',
      'Auto-enrich with email & phone',
      'Filter by location, category, rating',
      'One-click CRM export',
      'Bulk download as CSV',
    ],
    benefits: [
      { title: 'Real businesses', body: 'Source places from Google Maps — not placeholder lists.' },
      { title: 'CRM ready', body: 'Sync enriched contacts into your pipeline instantly.' },
      { title: 'Targeted ICP', body: 'Filter by niche, location, and quality signals.' },
    ],
    loginUrl: '/auth',
    appPath: '/lead-scraper',
  },
  {
    slug: 'modules',
    icon: 'Database',
    tag: 'ERP',
    title: 'Snagcart Modules',
    subtitle: 'Full Business ERP Backend',
    eyebrow: 'Operate the business',
    hero: 'E-commerce, CRM, inventory, accounting and 50+ modules under your brand.',
    desc: 'Access the full power of Snagcart ERP — e-commerce, CRM, inventory, accounting and 50+ business modules — all under your own brand.',
    features: [
      'E-commerce & online store',
      'CRM & sales pipeline',
      'Inventory & warehouse management',
      'Accounting & invoicing',
      'HR & payroll modules',
      '50+ Snagcart business apps',
    ],
    benefits: [
      { title: 'Backend depth', body: 'Grow past a landing page into full operations.' },
      { title: 'White-label ready', body: 'Present ERP modules under your brand.' },
      { title: 'Unified stack', body: 'Keep websites, marketing, and ops in one platform.' },
    ],
    loginUrl: '/auth',
    appPath: '/snagcart/apps',
  },
];

export function getProductBySlug(slug) {
  return productShowcases.find((p) => p.slug === slug) || null;
}

export function getRelatedProducts(slug, limit = 3) {
  const idx = productShowcases.findIndex((p) => p.slug === slug);
  if (idx < 0) return productShowcases.slice(0, limit);
  const out = [];
  for (let i = 1; i <= productShowcases.length && out.length < limit; i += 1) {
    out.push(productShowcases[(idx + i) % productShowcases.length]);
  }
  return out;
}
